import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronDown } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { CODE_TO_URL } from "../lib/i18nRouter";
import { SimplePage } from "./StaticPages";
import Seo from "../components/Seo";
import TX, { useTX } from "../components/TX";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";

// Translated search input for the FAQ — kept inline so we don't litter
// helpers across the file. Uses the useTX hook for the placeholder.
const FAQSearchInput = ({ value, onChange }) => {
  const t = useTX();
  return (
    <input
      data-testid="faq-search"
      type="text"
      placeholder={t("Search the FAQ — e.g. shipping, certificates, treatment…")}
      value={value}
      onChange={onChange}
      className="w-full bg-onyx border border-white/10 pl-12 pr-4 py-4 text-white placeholder:text-white/30 focus:outline-none focus:border-gold transition-colors text-base font-light"
    />
  );
};

/* ============================================================
 *  T R U S T   &   P O L I C I E S   ·   8   P A G E S
 *  Authenticity / Certification / Warranty / FAQ /
 *  Ethical Sourcing / Returns / Shipping / Auction Rules
 * ============================================================ */

// Shared elements ------------------------------------------------------------

const Pillar = ({ eyebrow, title, body }) => (
  <div className="border-l border-gold/60 pl-6">
    <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-3"><TX>{eyebrow}</TX></p>
    <h3 className="font-heading text-2xl text-ivory leading-snug mb-3"><TX>{title}</TX></h3>
    <p className="text-white/60 text-base leading-relaxed font-light"><TX>{body}</TX></p>
  </div>
);

const PolicyNotice = ({ title, children }) => (
  <div className="border border-gold/30 bg-onyx p-8 my-10">
    <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-3"><TX>{title}</TX></p>
    <div className="text-white/70 text-base leading-relaxed font-light">{children}</div>
  </div>
);

// ============================================================
//  1 · AUTHENTICITY
// ============================================================
export const Authenticity = () => {
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  return (
    <SimplePage
      id="authenticity"
      eyebrow="Trust & Policies"
      title="Authenticity, guaranteed."
      image="https://images.unsplash.com/photo-1735480166632-38176b26f212"
      lede="Every gemstone offered by OPIKJEWELS is independently verified by tier-one laboratories and accompanied by a lifetime guarantee of authenticity."
    >
      <Seo title="Authenticity Guarantee · OPIKJEWELS" description="Lifetime guarantee of authenticity. Every stone independently graded by GIA, SSEF, Gübelin, AGL, or GRS." path={`/${slug}/policies/authenticity`} />
      <p>The luxury gemstone market is, by its nature, a market of trust. A pigeon-blood Burmese ruby and a heated Mozambique stone of identical visual appearance can differ in value by a factor of fifty. For this reason, every stone listed by OPIKJEWELS undergoes a four-stage verification protocol before sale.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 my-12">
        <Pillar eyebrow="Stage 01" title="Origin Verification" body="Geographic origin is determined by inclusion analysis, trace-element spectroscopy, and UV-VIS fluorescence performed in-house by our senior gemologist." />
        <Pillar eyebrow="Stage 02" title="Treatment Disclosure" body="Any thermal, fissure-filling, oiling, or beryllium-lattice diffusion is disclosed in writing. Untreated stones are explicitly marked 'No Heat · No Treatment'." />
        <Pillar eyebrow="Stage 03" title="Laboratory Grading" body="Each stone is graded by GIA, SSEF, Gübelin, AGL, GRS, or Lotus. Reports are scanned at high resolution and linked to the stone's permanent record." />
        <Pillar eyebrow="Stage 04" title="Provenance Chain" body="From mine to vault, each transfer of custody is logged. Where mining provenance is available (Mogok permit, Kashmir registry), it is documented in the Gem Passport." />
      </div>

      <PolicyNotice title="Lifetime Authenticity Guarantee">
        If any stone purchased from OPIKJEWELS is ever determined by an independent tier-one laboratory to be misrepresented as to species, origin, or treatment status, we will repurchase the stone at the original sale price, in the original currency, in perpetuity.
      </PolicyNotice>

      <p>This guarantee is non-transferable in name but transferable in inheritance — your heirs hold the same protection you do. The guarantee is voided only by undisclosed re-cutting, re-treating, or substitution after sale.</p>

      <div className="mt-12 flex flex-wrap gap-4">
        <Link to={`/${slug}/policies/certification`} data-testid="link-to-certification" className="border border-gold/40 text-gold font-accent uppercase tracking-[0.28em] text-[11px] px-6 py-3 hover:bg-gold hover:text-black transition-colors">Read Certification</Link>
        <Link to={`/${slug}/verify`} data-testid="link-to-verify" className="border border-white/20 text-white/80 font-accent uppercase tracking-[0.28em] text-[11px] px-6 py-3 hover:border-gold hover:text-gold transition-colors">Verify a Certificate</Link>
      </div>
    </SimplePage>
  );
};

// ============================================================
//  2 · CERTIFICATION
// ============================================================
export const Certification = () => {
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  return (
    <SimplePage
      id="certification"
      eyebrow="Trust & Policies"
      title="Independent grading."
      image="https://images.unsplash.com/photo-1777126413608-f06fbd6e3208"
      lede="Every investment-grade stone is accompanied by a current report from a tier-one independent laboratory. We do not issue self-graded reports."
    >
      <Seo title="Certification · OPIKJEWELS" description="GIA, SSEF, Gübelin, AGL, GRS, Lotus — independent third-party gemological certification on every stone." path={`/${slug}/policies/certification`} />

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">Accepted laboratories</h2>
      <ul className="space-y-3 text-white/70">
        <li><span className="text-gold">GIA</span> — Gemological Institute of America (diamonds, coloured stones)</li>
        <li><span className="text-gold">SSEF</span> — Swiss Gemmological Institute (high-end coloured stones, pearls)</li>
        <li><span className="text-gold">Gübelin</span> — Gübelin Gem Lab, Lucerne (Kashmir / Mogok origin)</li>
        <li><span className="text-gold">AGL</span> — American Gemological Laboratories (coloured stones)</li>
        <li><span className="text-gold">GRS</span> — GemResearch Swisslab (rubies, sapphires)</li>
        <li><span className="text-gold">Lotus</span> — Lotus Gemology, Bangkok (rubies, sapphires, spinels)</li>
      </ul>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">What a report includes</h2>
      <p>A laboratory report typically certifies: species and variety, weight to two decimals, measurements in millimetres, cut and shape, colour grade or descriptor, clarity descriptor, treatment status, and — for premium stones — country or geographic origin.</p>

      <PolicyNotice title="A note on origin certificates">
        Origin determination is a scientific opinion, not a guarantee. Different laboratories may issue different origin opinions for the same stone. We default to the most conservative determination and always disclose disagreement between labs in writing.
      </PolicyNotice>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">Re-certification</h2>
      <p>If you wish to re-certify a stone purchased from OPIKJEWELS at a different laboratory, we can arrange this on your behalf. Submission, insurance, and laboratory fees are at the client's expense; we charge no handling premium.</p>

      <div className="mt-12">
        <Link to={`/${slug}/verify`} data-testid="link-to-verify-cert" className="border border-gold text-gold font-accent uppercase tracking-[0.28em] text-[11px] px-6 py-3 hover:bg-gold hover:text-black transition-colors">Verify a Certificate Online</Link>
      </div>
    </SimplePage>
  );
};

// ============================================================
//  3 · WARRANTY
// ============================================================
export const Warranty = () => {
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  return (
    <SimplePage
      id="warranty"
      eyebrow="Trust & Policies"
      title="Lifetime warranty."
      image="https://images.unsplash.com/photo-1763256614634-7feb3ff79ff3"
      lede="Every stone and finished jewel sold by OPIKJEWELS carries a lifetime warranty covering authenticity, manufacturing defects, and aftercare."
    >
      <Seo title="Warranty · OPIKJEWELS" description="Lifetime warranty on authenticity, manufacturing defects, and aftercare for every OPIKJEWELS gemstone and jewel." path={`/${slug}/policies/warranty`} />

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">What is covered</h2>
      <ul className="space-y-3 text-white/70 list-none">
        <li className="border-l border-gold pl-5"><span className="text-ivory">Authenticity</span> — repurchase guarantee at original price if any laboratory misrepresentation is proven.</li>
        <li className="border-l border-gold pl-5"><span className="text-ivory">Manufacturing defects</span> — settings, prongs, and clasps repaired at no charge for the life of the jewel.</li>
        <li className="border-l border-gold pl-5"><span className="text-ivory">Annual maintenance</span> — complimentary cleaning, polishing, and prong inspection once per year, in perpetuity.</li>
        <li className="border-l border-gold pl-5"><span className="text-ivory">Resizing</span> — first resizing within 12 months of purchase is complimentary.</li>
      </ul>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">What is not covered</h2>
      <ul className="space-y-2 text-white/60 list-disc pl-6">
        <li>Loss or theft (covered by your insurance policy, not by us)</li>
        <li>Damage caused by misuse, impact, or chemical exposure</li>
        <li>Wear from normal use of softer stones (opal, emerald, tanzanite)</li>
        <li>Repairs performed by third-party jewelers</li>
      </ul>

      <PolicyNotice title="Insurance recommendation">
        We recommend that clients consider an independent valuables insurance policy for long-term home storage of any stone above USD 5,000. We can introduce you to specialist underwriters in Jakarta, Singapore, Dubai, London, and New York on request.
      </PolicyNotice>

      <p className="mt-8">To activate any warranty service, contact your concierge with your Gem Passport reference number and a brief description of the work required.</p>
    </SimplePage>
  );
};

// ============================================================
//  4 · FAQ  (six sections, 18 questions, searchable, accordion)
// ============================================================
const FAQ_SECTIONS = [
  {
    id: "general",
    title: "General",
    eyebrow: "01",
    items: [
      {
        q: "Are the gemstones natural?",
        a: "OPIKJEWELS curates three categories of stones — natural earth-mined, treated, and laboratory-grown — and each piece is clearly labelled. The category, any treatments applied (heating, oiling, diffusion, irradiation, etc.), and the growth origin (natural vs lab-grown) are disclosed in plain language on every product description, on the invoice, and on the accompanying laboratory report. We never blur the line: a natural stone is sold as natural, a treated stone is sold as treated, and a lab-grown stone is sold as lab-grown — at the price each truly commands.",
      },
      {
        q: "Do stones come with certificates?",
        a: "Every gemstone above 0.50 ct in the OPIKJEWELS collection ships with an original laboratory report from one of the accepted labs listed in our Certification policy. Smaller accent stones used in finished jewellery are covered by our in-house identification report. All certificates can be cross-referenced online via /verify.",
      },
      {
        q: "What is the difference between treated and untreated gemstones?",
        a: "An untreated gemstone is offered to the market in exactly the state in which it left the earth, after cutting and polishing only. A treated gemstone has undergone an industry-recognised enhancement — most commonly thermal treatment (heating) to improve colour or clarity. Treatments are disclosed in writing on every invoice and certificate. Untreated stones of fine colour command significant premiums and form the core of our investment-grade inventory.",
      },
    ],
  },
  {
    id: "purchasing",
    title: "Purchasing",
    eyebrow: "02",
    items: [
      {
        q: "How do I choose the right gemstone?",
        a: "Begin with intent: are you collecting for beauty, investment, milestone gifting, or astrological alignment? Our concierge will then guide you through the four pillars of value — origin, colour, clarity, and carat — within your preferred budget. We offer complimentary one-on-one consultations, in person in Jakarta and Bangkok or by encrypted video globally.",
      },
      {
        q: "Can I reserve an auction lot before bidding opens?",
        a: "Lots are not held off the auction floor, but registered VIP clients may submit absentee bids, telephone-bidding requests, and proxy maximums in advance. To register for an upcoming sale, complete KYC verification and request VIP status via your account.",
      },
      {
        q: "Do you offer bespoke jewellery and customisation?",
        a: "Yes. Our atelier creates one-of-a-kind settings around stones you select from our inventory or sources you supply. Lead time is typically 8–14 weeks. The process includes design renderings, wax models, and a final approval review before casting. Contact the concierge to initiate a bespoke commission.",
      },
    ],
  },
  {
    id: "shipping",
    title: "Shipping",
    eyebrow: "03",
    items: [
      {
        q: "How long does international shipping take?",
        a: "Most international shipments are dispatched within 48 hours of cleared payment and full KYC. Delivery times vary by destination: 2–4 business days for major hubs (Singapore, Hong Kong, Dubai, London, New York), 5–8 business days elsewhere. All consignments are sent by trusted secured carriers (DHL Express · FedEx Priority for everyday shipments; Brink's, Malca-Amit, or Ferrari Group for high-value consignments) with signature on receipt.",
      },
      {
        q: "Is the shipment insured?",
        a: "Every consignment is fully insured door-to-door at declared value through our shipping carrier's standard insurance programme, at no additional cost to the client. A certificate of insurance is issued with each shipment.",
      },
      {
        q: "Who is responsible for customs duties and import taxes?",
        a: "Import duties, VAT/GST, and any destination-country taxes are the responsibility of the buyer, in accordance with international trade norms. We will, on request, prepare commercial invoices, certificates of origin, and Kimberley Process certificates (for diamonds) to assist your customs broker.",
      },
    ],
  },
  {
    id: "authenticity",
    title: "Authenticity",
    eyebrow: "04",
    items: [
      {
        q: "How is authenticity verified?",
        a: "Our in-house gemologist conducts a four-stage protocol on every stone: origin verification, treatment screening, weight and dimension confirmation, and cross-reference with the laboratory report. Each stone is photographed under standardised conditions and assigned a permanent Gem Passport ID.",
      },
      {
        q: "What does a laboratory certificate actually confirm?",
        a: "A laboratory report from GIA / SSEF / Gübelin / AGL / GRS / Lotus is the gemological equivalent of an audited financial statement. It certifies — by repeatable, instrument-based testing — the stone's species, weight, dimensions, colour grade, clarity, cut, treatment status, and (for premium stones) geographic origin. It does not appraise commercial value.",
      },
      {
        q: "What is the return inspection policy?",
        a: "Every returned stone is inspected on arrival by two independent gemologists who compare the returned item against the original Gem Passport photographs, measurements, weight, and certificate. Discrepancies — including unauthorised re-cutting, re-setting, or substitution — void return eligibility. Findings are documented in writing and shared with the client.",
      },
    ],
  },
  {
    id: "care",
    title: "Care Guide",
    eyebrow: "05",
    items: [
      {
        q: "How should I clean my gemstones at home?",
        a: "For diamonds, sapphires, rubies, and spinels: warm water, a drop of mild dish soap, and a soft toothbrush. Rinse and pat dry with a lint-free cloth. For softer or porous stones — emerald, opal, pearl, turquoise — use only a damp microfibre cloth and never ultrasonic or steam cleaners. When in doubt, return the piece to us for complimentary professional cleaning.",
      },
      {
        q: "How should I store my fine jewellery?",
        a: "Store each piece individually in a soft pouch or padded compartment to prevent scratches. Keep away from direct sunlight, heat, and humidity. For pieces above USD 10,000, we recommend a UL-rated home safe or a bank deposit box. Always remove jewellery before sleeping, exercising, swimming, or handling chemicals.",
      },
      {
        q: "What ongoing maintenance do you recommend?",
        a: "Annual professional inspection — included for life on every OPIKJEWELS piece. We check prong integrity, clasp function, and re-polish the metal. For pieces worn daily, additional inspection every six months is prudent. Have any loose stone re-set immediately; never wear jewellery you suspect is compromised.",
      },
    ],
  },
  {
    id: "astrology",
    title: "Astrology & Birthstones",
    eyebrow: "06",
    items: [
      {
        q: "How does a birthstone work?",
        a: "Birthstones associate each calendar month — and in some traditions each zodiac sign — with a particular gemstone believed to amplify the qualities of that period. The modern Western birthstone list was standardised by the American National Association of Jewelers in 1912; older Vedic and Tibetan traditions assign stones differently. Many of our clients enjoy birthstones as personal symbolism alongside their core collection.",
      },
      {
        q: "What is the meaning of a zodiac gemstone?",
        a: "Zodiac gemstones are stones traditionally aligned with each astrological sign — for example, ruby for Leo, sapphire for Virgo, emerald for Taurus. The associations vary between Western and Vedic traditions. You may consult the OPIKJEWELS AI gemologist for a personalised mapping based on your birth date and chart.",
      },
      {
        q: "Can a gemstone change my luck or fortune?",
        a: "OPIKJEWELS does not make metaphysical or therapeutic claims. We acknowledge — and respect — the centuries-old cultural and spiritual traditions surrounding gemstones in Asia, the Middle East, and beyond. Many of our clients select stones for their symbolism. Whether or not a stone influences fortune is a matter of personal belief, not of gemology.",
      },
    ],
  },
];

export const FAQ = () => {
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  const [query, setQuery] = useState("");

  // Filter Q&A items by query — match against question and answer text
  const sections = (() => {
    if (!query.trim()) return FAQ_SECTIONS;
    const q = query.trim().toLowerCase();
    return FAQ_SECTIONS
      .map((s) => ({
        ...s,
        items: s.items.filter(
          (it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q)
        ),
      }))
      .filter((s) => s.items.length > 0);
  })();

  return (
    <div data-testid="page-faq" className="pt-32">
      <Seo
        title="Frequently Asked Questions · OPIKJEWELS"
        description="Trusted answers on gemstones, certification, shipping, authenticity, care, and astrology — from the OPIKJEWELS gemologists."
        path={`/${slug}/policies/faq`}
      />

      <div className="relative h-[55vh] overflow-hidden mb-16">
        <img
          src="https://images.unsplash.com/photo-1762019313711-8b5d1e4f7ba4"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black"></div>
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 h-full flex flex-col justify-end pb-16">
          <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5"><TX>Trust & Policies</TX></p>
          <h1 className="font-heading font-light text-5xl md:text-7xl leading-[1.05]">
            <TX>Frequently asked questions.</TX>
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mt-6 font-light">
            <TX>Trusted answers from our gemologists — on stones, certification, shipping, authenticity, care, and astrology.</TX>
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 pb-32">
        {/* Search */}
        <div className="relative mb-10">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
          <FAQSearchInput value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>

        {/* Section index */}
        {!query.trim() && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-14">
            {FAQ_SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#section-${s.id}`}
                data-testid={`faq-jump-${s.id}`}
                className="group border border-white/10 hover:border-gold/40 bg-onyx px-5 py-4 transition-colors"
              >
                <span className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold"><TX>{s.eyebrow}</TX></span>
                <div className="font-heading text-lg text-ivory mt-1 group-hover:text-gold transition-colors"><TX>{s.title}</TX></div>
              </a>
            ))}
          </div>
        )}

        {/* Sections + Accordion */}
        {sections.length === 0 ? (
          <div data-testid="faq-no-results" className="border border-white/10 bg-onyx p-12 text-center">
            <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-white/40 mb-3"><TX>No matches</TX></p>
            <p className="text-white/70 font-light">
              <TX>Nothing matched your search. Try a broader term — or write to</TX>{" "}
              <Link to={`/${slug}/contact`} className="text-gold hover:underline">info@opikjewels.com</Link>.
            </p>
          </div>
        ) : (
          sections.map((s) => (
            <section key={s.id} id={`section-${s.id}`} className="mb-16 scroll-mt-32">
              <div className="flex items-baseline gap-4 mb-6 pb-4 border-b border-white/10">
                <span className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold"><TX>{s.eyebrow}</TX></span>
                <h2 className="font-heading text-3xl text-ivory"><TX>{s.title}</TX></h2>
              </div>

              <Accordion type="multiple" className="space-y-2">
                {s.items.map((it, idx) => {
                  const key = `${s.id}-${idx}`;
                  return (
                    <AccordionItem
                      key={key}
                      value={key}
                      data-testid={`faq-item-${key}`}
                      className="border border-white/10 hover:border-gold/30 bg-onyx px-6 transition-colors"
                    >
                      <AccordionTrigger className="font-heading text-lg text-ivory hover:no-underline py-5 [&[data-state=open]>svg]:text-gold">
                        <TX>{it.q}</TX>
                      </AccordionTrigger>
                      <AccordionContent className="text-white/70 text-base leading-relaxed font-light pb-6 pt-1">
                        <TX>{it.a}</TX>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </section>
          ))
        )}

        <div className="mt-16 border-t border-white/10 pt-10 text-center">
          <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-4"><TX>Still curious?</TX></p>
          <p className="text-white/70 font-light mb-6">
            <TX>Speak privately with a senior gemologist. Discretion absolute.</TX>
          </p>
          <Link
            to={`/${slug}/contact`}
            data-testid="faq-contact-cta"
            className="inline-block bg-gold text-black font-accent uppercase tracking-[0.28em] text-[11px] px-8 py-4 hover:bg-ivory transition-colors"
          >
            <TX>Contact the Concierge</TX>
          </Link>
        </div>
      </div>
    </div>
  );
};

// ============================================================
//  5 · ETHICAL SOURCING
// ============================================================
export const EthicalSourcing = () => {
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  return (
    <SimplePage
      id="ethical-sourcing"
      eyebrow="Trust & Policies"
      title="Ethically sourced. Always."
      image="https://images.unsplash.com/photo-1705575420317-daaed2ab4cf3"
      lede="OPIKJEWELS sources stones only from mines and dealers that meet, or exceed, international standards for human rights, environmental stewardship, and chain-of-custody transparency."
    >
      <Seo title="Ethical Sourcing · OPIKJEWELS" description="Kimberley Process, RJC chain-of-custody, fair-trade artisanal partnerships, and full provenance disclosure." path={`/${slug}/policies/ethical-sourcing`} />

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">Our framework</h2>
      <ul className="space-y-3 text-white/70">
        <li><span className="text-gold">Kimberley Process</span> — every rough and polished diamond entering our inventory carries a Kimberley Process Certification Scheme (KPCS) certificate.</li>
        <li><span className="text-gold">RJC Code of Practices</span> — we comply with the Responsible Jewellery Council's chain-of-custody standards.</li>
        <li><span className="text-gold">OECD Due Diligence Guidance</span> — our coloured-stone supply chain follows OECD due-diligence procedures for mineral sourcing from high-risk areas.</li>
        <li><span className="text-gold">Fair-Trade Partnerships</span> — small-scale artisanal supply from Mogok, Ratnapura, and Muzo is conducted through co-operatives that pay licensed miner wages and reinvest in local infrastructure.</li>
      </ul>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">What we do not buy</h2>
      <ul className="space-y-2 text-white/60 list-disc pl-6">
        <li>Conflict diamonds from any jurisdiction</li>
        <li>Stones of unverifiable origin from sanctioned regions</li>
        <li>Material extracted using child or forced labour</li>
        <li>Coral, ivory, or any other non-mineral protected substance</li>
      </ul>

      <PolicyNotice title="Provenance disclosure">
        Where mining provenance has been independently verified (mine permit, government export documentation, miner co-operative declaration), it is recorded in the Gem Passport. Where it is not verifiable, we state so plainly — we will never invent a story to lift a stone's narrative.
      </PolicyNotice>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">Environmental commitment</h2>
      <p>We offset all shipping carbon through verified Gold Standard projects, recycle 100% of precious-metal scrap from our atelier, and prefer recycled platinum and recycled 18k gold for new commissions wherever the client agrees.</p>
    </SimplePage>
  );
};

// ============================================================
//  6 · RETURNS
// ============================================================
export const Returns = () => {
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  return (
    <SimplePage
      id="returns"
      eyebrow="Trust & Policies"
      title="Return policy."
      image="https://images.pexels.com/photos/21235148/pexels-photo-21235148.jpeg"
      lede="Loose gemstones and ready-to-wear jewellery may be returned within seven (7) calendar days of delivery, subject to the conditions below. Auction lots and bespoke commissions are final sale."
    >
      <Seo title="Returns Policy · OPIKJEWELS" description="7-day inspection right on stock items. Auction lots and bespoke commissions final sale." path={`/${slug}/policies/returns`} />

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">Eligible items</h2>
      <ul className="space-y-2 text-white/70 list-disc pl-6">
        <li>Loose gemstones purchased from inventory listings</li>
        <li>Ready-to-wear jewellery from our atelier collection</li>
      </ul>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">Not eligible</h2>
      <ul className="space-y-2 text-white/60 list-disc pl-6">
        <li>All auction lots — final sale per Auction Rules</li>
        <li>Bespoke and custom commissions</li>
        <li>Re-sized, re-set, or modified jewellery</li>
        <li>Items showing signs of wear or third-party intervention</li>
      </ul>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">Process</h2>
      <ol className="space-y-3 text-white/70 list-decimal pl-6">
        <li>Notify your concierge in writing within 7 calendar days of receipt.</li>
        <li>You will receive a return authorisation number and pre-paid insured shipping label (Brink's or Malca-Amit).</li>
        <li>Return the item in original packaging, with all certificates and the original Gem Passport.</li>
        <li>On arrival, two independent gemologists inspect the stone against original photographs and weight.</li>
        <li>If approved, refund is issued to the original payment method within 7 business days.</li>
      </ol>

      <PolicyNotice title="Return shipping costs">
        Pre-paid insured return shipping is complimentary for orders above USD 10,000. For smaller orders, return logistics are at the client's cost; we will arrange the carrier on your behalf.
      </PolicyNotice>

      <p>A 5% restocking fee may apply to discretionary returns of items over USD 50,000. There is no restocking fee for items returned due to laboratory misrepresentation or shipping damage.</p>
    </SimplePage>
  );
};

// ============================================================
//  7 · SHIPPING
// ============================================================
export const Shipping = () => {
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  return (
    <SimplePage
      id="shipping"
      eyebrow="Trust & Policies"
      title="Secured global shipping."
      image="https://images.unsplash.com/photo-1762019313711-8b5d1e4f7ba4"
      lede="Every shipment is dispatched by trusted secured carriers (Brink's · Malca-Amit · Ferrari Group · DHL Express · FedEx Priority depending on destination) with full carrier insurance at declared value, door to door."
    >
      <Seo title="Shipping · OPIKJEWELS" description="Brink's, Malca-Amit, Ferrari Group, DHL Express and FedEx Priority secured shipping with full carrier insurance to 175 countries." path={`/${slug}/policies/shipping`} />

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">Carriers</h2>
      <p>We use trusted secured carriers matched to consignment value: <span className="text-gold">DHL Express</span> and <span className="text-gold">FedEx Priority</span> with signature-on-delivery for everyday shipments, and <span className="text-gold">Brink's Global Services</span>, <span className="text-gold">Malca-Amit</span>, or <span className="text-gold">Ferrari Group</span> precious-cargo specialists for high-value consignments. Every shipment is fully insured by the carrier at declared value.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">Transit time</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
        <div className="border-l border-gold pl-5">
          <div className="font-heading text-2xl text-ivory">2–4 business days</div>
          <div className="text-white/50 text-sm mt-1">Singapore · Hong Kong · Dubai · London · New York · Tokyo · Sydney</div>
        </div>
        <div className="border-l border-gold pl-5">
          <div className="font-heading text-2xl text-ivory">5–8 business days</div>
          <div className="text-white/50 text-sm mt-1">All other destinations covered by carrier network (175+ countries)</div>
        </div>
      </div>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">Insurance</h2>
      <p>Every shipment is fully insured by our shipping carrier at the declared sale value, door to door, with no additional charge to the client. A certificate of insurance is supplied with each consignment. Higher coverage (specialist underwriting) is available on request for high-value consignments.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">Customs duties & import taxes</h2>
      <p>Import duties, VAT, GST, and any other destination-country taxes are the responsibility of the buyer. We prepare full commercial documentation — invoice, certificate of origin, Kimberley Process certificate (where applicable), and laboratory reports — to assist your customs broker. On request, we can also coordinate with your broker directly.</p>

      <PolicyNotice title="Free zones & bonded warehousing">
        For collectors who wish to acquire investment-grade stones without triggering domestic import liability, we can deliver to free-trade-zone vaults in Singapore, Geneva, Hong Kong, Dubai, and Delaware.
      </PolicyNotice>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">Signature & identity verification</h2>
      <p>All consignments require photo-ID and signature on receipt by the named recipient. Alternate authorised recipients can be designated in advance through your concierge. Shipments are never left at door, with neighbours, or in mailbox facilities.</p>
    </SimplePage>
  );
};

// ============================================================
//  8 · AUCTION RULES
// ============================================================
export const AuctionRules = () => {
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  return (
    <SimplePage
      id="auction-rules"
      eyebrow="Trust & Policies"
      title="Auction rules & terms."
      image="https://images.unsplash.com/photo-1735480166632-38176b26f212"
      lede="The following terms govern all OPIKJEWELS live and online auctions. Registration to bid constitutes acceptance of these terms."
    >
      <Seo title="Auction Rules · OPIKJEWELS" description="Registration, bidding, increments, buyer's premium, reserve, settlement, and dispute resolution for OPIKJEWELS auctions." path={`/${slug}/policies/auction-rules`} />

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">1 · Registration</h2>
      <p>All bidders must complete identity verification (KYC) and have an active OPIKJEWELS account. Bidders for lots with a low estimate above USD 50,000 must provide a financial reference or refundable deposit of 10% of the low estimate before bidding privileges are activated.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">2 · Bid increments</h2>
      <p>Bids advance in increments scaled to the current price level. Indicative scale:</p>
      <ul className="space-y-1 text-white/70 list-none my-5 font-mono text-sm">
        <li>Below USD 5,000 — USD 200</li>
        <li>USD 5,000 – 20,000 — USD 500</li>
        <li>USD 20,000 – 100,000 — USD 2,000</li>
        <li>USD 100,000 – 500,000 — USD 10,000</li>
        <li>Above USD 500,000 — USD 25,000</li>
      </ul>
      <p>The auctioneer retains discretion to vary increments to maintain pace.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">3 · Reserve</h2>
      <p>Most lots are offered subject to a confidential reserve — the minimum price the consignor will accept. The reserve never exceeds the published low estimate. Lots that fail to meet reserve are "passed" and may be re-offered, sold post-auction at negotiated terms, or returned to the consignor.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">4 · Buyer's premium</h2>
      <p>A buyer's premium of <span className="text-gold">22%</span> is added to the hammer price, applicable to all lots. The premium covers cataloguing, gemological verification, photography, marketing, and platform infrastructure. The published catalogue estimate excludes buyer's premium.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">5 · Settlement</h2>
      <p>Successful bidders must settle within <span className="text-ivory">7 calendar days</span> of the hammer. Accepted methods: bank wire (Bank Mandiri Jakarta or Bangkok counterparties), Stripe for amounts under USD 50,000. Title transfers only on cleared funds.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">6 · Final sale</h2>
      <p>All auction sales are <span className="text-ivory">final</span>. The standard 7-day return policy applicable to inventory listings does not apply to lots won at auction. The Lifetime Authenticity Guarantee (see Authenticity policy) continues to apply.</p>

      <PolicyNotice title="Absentee, telephone, and proxy bids">
        VIP-registered clients may bid by absentee (written maximum), telephone (live agent during the sale), or proxy (algorithmic auto-bid up to a stated ceiling). Requests must be received at least 24 hours before the sale commences.
      </PolicyNotice>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">7 · Dispute resolution</h2>
      <p>Any dispute is governed by the laws of the Republic of Singapore and subject to arbitration under the rules of the Singapore International Arbitration Centre (SIAC) in Singapore, in English, by a sole arbitrator.</p>

      <div className="mt-12">
        <Link to={`/${slug}/auctions`} data-testid="link-to-auctions" className="border border-gold text-gold font-accent uppercase tracking-[0.28em] text-[11px] px-6 py-3 hover:bg-gold hover:text-black transition-colors">View Live Auctions</Link>
      </div>
    </SimplePage>
  );
};

// ============================================================
//  9 · PRIVACY POLICY
// ============================================================
export const Privacy = () => {
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  return (
    <SimplePage
      id="privacy"
      eyebrow="Trust & Policies"
      title="Privacy policy."
      image="https://images.unsplash.com/photo-1735480166632-38176b26f212"
      lede="OPIKJEWELS handles the personal information of its clients with the same discretion it applies to its stones. This policy explains what we collect, why, and the rights you hold."
    >
      <Seo title="Privacy Policy · OPIKJEWELS" description="GDPR-aligned · UU PDP Indonesia-compliant privacy policy for clients of OPIKJEWELS." path={`/${slug}/policies/privacy`} />

      <p className="text-white/40 text-sm">Last updated: 13 May 2026 · Effective: 13 May 2026</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">1 · Who we are</h2>
      <p>OPIKJEWELS ("we", "our", "the Maison") is a luxury gemstone house with its registered office in Jakarta, Indonesia. For the purposes of EU GDPR and Indonesia's UU 27/2022 (Personal Data Protection Law), OPIKJEWELS is the data controller for the personal information described in this policy.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">2 · What we collect</h2>
      <ul className="space-y-2 text-white/70 list-disc pl-6">
        <li>Identity data — name, photo ID, proof of address (for KYC on orders above USD 50,000)</li>
        <li>Contact data — email, phone, shipping address</li>
        <li>Financial data — payment method, card token via Stripe / PayPal (we never see your full card number)</li>
        <li>Transactional data — your purchase history, wishlist, auction bids, certificate verifications</li>
        <li>Technical data — IP address, browser type, device, language, anonymised cookie identifiers</li>
        <li>Marketing data — your communication preferences and consent records</li>
      </ul>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">3 · Why we use it</h2>
      <ul className="space-y-2 text-white/70 list-disc pl-6">
        <li>To fulfil your order, ship your stone, and provide warranty / aftercare</li>
        <li>To comply with KYC, anti-money-laundering, and export-control obligations</li>
        <li>To send transactional emails (receipts, shipping updates, certificate notifications)</li>
        <li>To send curated marketing communications — only with your explicit opt-in</li>
        <li>To protect against fraud, account abuse, and platform misuse</li>
        <li>To improve our website, conversion funnel, and concierge experience</li>
      </ul>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">4 · Who we share with</h2>
      <p>We share the minimum data required to deliver your order with the following carefully-vetted processors: Stripe (payments), PayPal (payments), Resend (transactional email), Cloudinary (image hosting), Brink's / Malca-Amit / Ferrari Group / DHL / FedEx (secured shipping & insurance), our certified gemological partners (GIA / SSEF / Gübelin / AGL / GRS / Lotus), and Anthropic / OpenAI (AI gemologist conversations). We never sell your personal data — full stop.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">5 · Cookies & tracking</h2>
      <p>We use first-party cookies for authentication (your login session), shopping preferences (your currency and language), and analytics (PostHog). With your consent, we also use third-party advertising pixels — Meta (Facebook / Instagram) and TikTok — to measure ad campaign performance. You can decline these in your browser settings.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">6 · International transfers</h2>
      <p>Because OPIKJEWELS serves clients in 175+ countries and uses global infrastructure, your data may be processed in Indonesia, the United States, the European Union, and Singapore. All transfers are protected by Standard Contractual Clauses (where applicable) and equivalent technical safeguards.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">7 · How long we keep it</h2>
      <ul className="space-y-2 text-white/70 list-disc pl-6">
        <li>Order and warranty records — 10 years (Indonesian commercial law requirement)</li>
        <li>KYC documentation — 7 years (AML obligation)</li>
        <li>Marketing data — until you unsubscribe + 30-day grace period</li>
        <li>Website analytics — 26 months in anonymised form</li>
      </ul>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">8 · Your rights</h2>
      <p>Under GDPR and UU PDP Indonesia you have the right to: access the data we hold on you, correct inaccurate data, request deletion (subject to legal retention obligations), restrict or object to processing, port your data to another service, and withdraw consent at any time. To exercise any of these rights, write to <span className="text-gold">info@opikjewels.com</span> with the subject line "Privacy request — [your right]". We respond within 30 calendar days.</p>

      <PolicyNotice title="Data Protection Officer">
        Questions about this policy? Write to our Data Protection Officer at <span className="text-gold">info@opikjewels.com</span>. EU clients have the right to lodge a complaint with their national supervisory authority. Indonesian clients may complain to Kominfo (Kementerian Komunikasi dan Informatika).
      </PolicyNotice>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">9 · Children</h2>
      <p>Our services are not directed at, nor offered to, persons under 18 years of age. We do not knowingly collect personal data from minors.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">10 · Changes to this policy</h2>
      <p>We may update this policy from time to time. The version date at the top of this page reflects the most recent revision. Material changes are announced by email at least 14 days before they take effect.</p>
    </SimplePage>
  );
};

// ============================================================
//  10 · TERMS & CONDITIONS
// ============================================================
export const Terms = () => {
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  return (
    <SimplePage
      id="terms"
      eyebrow="Trust & Policies"
      title="Terms & conditions."
      image="https://images.unsplash.com/photo-1777126413608-f06fbd6e3208"
      lede="The following terms govern your use of opikjewels.com and any purchase, auction bid, or service obtained through our website. Continued use of the website constitutes acceptance."
    >
      <Seo title="Terms & Conditions · OPIKJEWELS" description="Terms governing purchases, auctions, payments, shipping, and use of opikjewels.com." path={`/${slug}/policies/terms`} />

      <p className="text-white/40 text-sm">Last updated: 13 May 2026</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">1 · The parties</h2>
      <p>"OPIKJEWELS", "we", "our", and "the Maison" refer to the gemstone house operating from Jakarta Gems Center, Pintu Bacan, Jakarta 13320, Indonesia. "Client", "you", and "your" refer to any visitor, registered user, or buyer using opikjewels.com.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">2 · Eligibility</h2>
      <p>You must be at least 18 years old and legally able to enter into binding contracts in your jurisdiction. By creating an account you confirm you meet these conditions.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">3 · Products & disclosure</h2>
      <p>OPIKJEWELS offers three categories of gemstones — natural earth-mined, treated, and laboratory-grown — and each piece is clearly labelled in its product description, invoice, and laboratory report. Specifications, photographs, and certificates are presented in good faith but may include minor variations inherent to natural and handcrafted materials.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">4 · Pricing & currency</h2>
      <p>Prices are displayed in multiple currencies (USD, EUR, CHF, HKD, AED, IDR, THB) using live FX rates from exchangerate.host. The base settlement currency is USD; non-USD displays are indicative until the moment of checkout, at which point the displayed currency becomes the contractual amount. All prices are <span className="text-ivory">exclusive of</span> shipping, insurance, import duties, and destination-country taxes.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">5 · Payment</h2>
      <p>We accept Stripe (cards), PayPal, and Bank Transfer (Bank Mandiri Jakarta · Acct 0060013181452 · Opik Permata). Title to any stone transfers <span className="text-ivory">only</span> upon cleared funds. For Bank Transfer, you must complete payment within 7 calendar days of order; otherwise the order is cancelled.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">6 · KYC verification</h2>
      <p>For purchases of USD 50,000 or more, we require Know-Your-Customer verification — government-issued ID and proof of address — before dispatch. This protects all parties from fraud and complies with Indonesian and international anti-money-laundering regulations.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">7 · Auctions</h2>
      <p>Auctions are governed by the dedicated <Link to={`/${slug}/policies/auction-rules`} className="text-gold hover:underline">Auction Rules</Link>, incorporated into these Terms by reference. All auction sales are final.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">8 · Shipping & delivery</h2>
      <p>All shipments are dispatched by secured carriers (Brink's, Malca-Amit, Ferrari Group, DHL Express or FedEx Priority depending on destination and consignment value), fully insured at declared value under the carrier's standard insurance programme. Standard transit is 2–8 business days depending on destination. See the <Link to={`/${slug}/policies/shipping`} className="text-gold hover:underline">Shipping Policy</Link> for full details. Import duties and destination-country taxes are the responsibility of the buyer.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">9 · Returns & refunds</h2>
      <p>Loose gemstones and ready-to-wear jewellery from inventory may be returned within 7 calendar days of receipt, subject to the conditions in the <Link to={`/${slug}/policies/returns`} className="text-gold hover:underline">Returns & Refunds Policy</Link>. Auction lots and bespoke commissions are final sale.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">10 · Warranty</h2>
      <p>Every stone and finished jewel carries a lifetime warranty per the <Link to={`/${slug}/policies/warranty`} className="text-gold hover:underline">Warranty Policy</Link>.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">11 · Intellectual property</h2>
      <p>All content on opikjewels.com — photography, text, logos, the OPIKJEWELS name and trademarks — is the property of OPIKJEWELS or its licensors and is protected by international copyright and trademark law. You may not reproduce, distribute, or commercially exploit any content without our prior written consent.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">12 · Limitation of liability</h2>
      <p>To the maximum extent permitted by law, OPIKJEWELS' total aggregate liability arising out of or in connection with any single order shall not exceed the amount paid by you for that order. We exclude liability for indirect, incidental, consequential, or punitive damages, except in cases of gross negligence or wilful misconduct. The Lifetime Authenticity Guarantee remains in effect regardless of this clause.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">13 · Force majeure</h2>
      <p>Neither party shall be liable for delays or failures caused by events beyond reasonable control, including natural disasters, war, civil unrest, pandemics, government actions, currency controls, or carrier disruptions.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">14 · Governing law & jurisdiction</h2>
      <p>These Terms are governed by the laws of the Republic of Singapore. Any dispute shall be resolved by arbitration under the rules of the Singapore International Arbitration Centre (SIAC), in English, by a sole arbitrator, with the seat of arbitration in Singapore.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">15 · Contact</h2>
      <p>Questions about these Terms? Write to <span className="text-gold">info@opikjewels.com</span> or WhatsApp <span className="text-gold">+62 877 8105 8814</span>.</p>
    </SimplePage>
  );
};

// ============================================================
//  11 · DISCLAIMER (Gemstone treatments, lab-grown, investment, astrology)
// ============================================================
export const Disclaimer = () => {
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  return (
    <SimplePage
      id="disclaimer"
      eyebrow="Trust & Policies"
      title="Disclaimer."
      image="https://images.unsplash.com/photo-1763256614634-7feb3ff79ff3"
      lede="OPIKJEWELS believes in absolute transparency. The following disclosures clarify the nature of our products and the limits of any representation we make."
    >
      <Seo title="Disclaimer · OPIKJEWELS" description="Full disclosure on gemstone treatments, natural vs lab-grown, investment expectations, and astrology content." path={`/${slug}/policies/disclaimer`} />

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">1 · Natural · treated · laboratory-grown</h2>
      <p>OPIKJEWELS curates three categories of gemstones: <span className="text-ivory">natural earth-mined</span>, <span className="text-ivory">treated</span> (heated, oiled, fissure-filled, diffused, irradiated, etc.), and <span className="text-ivory">laboratory-grown</span>. Each piece is clearly labelled, with the category, any treatment applied, and the growth origin disclosed on every product description, invoice, and laboratory report. The three categories command different price ranges, and the difference is explicit in our pricing.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">2 · Origin determination</h2>
      <p>Geographic origin (e.g. "Mogok, Burma" or "Kashmir") is a scientific opinion issued by an independent laboratory, not a guarantee. Different laboratories may issue different origin opinions for the same stone. We default to the most conservative determination and disclose disagreement between labs in writing.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">3 · Colour & photography</h2>
      <p>Despite our best efforts to photograph stones under standardised laboratory conditions, the colour you see on screen may differ slightly from the stone in person. This is inherent to digital displays, ambient lighting, and the unique optical character of every gemstone. We provide unlimited HD video on request and full return rights on inventory items.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">4 · Investment performance</h2>
      <p>Where we reference historical price performance of investment-grade gemstones (e.g. "7–18% CAGR over 10 years"), this reflects published auction-house and market-research data for top-tier stones. <span className="text-ivory">Past performance is not a guarantee of future results.</span> Gemstone values fluctuate with global demand, macroeconomic conditions, fashion, and laboratory-treatment disclosure changes. OPIKJEWELS is not a financial advisor and our content does not constitute investment advice.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">5 · Astrology & birthstones</h2>
      <p>The OPIKJEWELS AI Gemologist and certain editorial content reference astrological traditions, zodiac stone associations, and Vedic / Western birthstone symbolism. <span className="text-ivory">These are cultural and spiritual traditions, not scientific claims.</span> OPIKJEWELS does not represent that any gemstone has the power to influence health, wealth, luck, fortune, or any other outcome. Such content is provided for cultural appreciation and personal symbolism only.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">6 · Medical claims</h2>
      <p>No gemstone offered by OPIKJEWELS is sold as a medical, therapeutic, or healing product. Any claim to the contrary made by third parties is not endorsed by us.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">7 · AI-generated content</h2>
      <p>Some content on opikjewels.com — including the AI Gemologist conversations and astrology readings — is generated by large language models. AI-generated content is provided "as is" and may occasionally contain inaccuracies. Critical decisions about a stone (origin, treatment, value) should always be confirmed by an independent laboratory report and a human gemologist.</p>

      <h2 className="font-heading text-3xl text-ivory mt-12 mb-5">8 · Third-party links</h2>
      <p>opikjewels.com may contain links to third-party websites (laboratory verification portals, news articles, social media). OPIKJEWELS is not responsible for the content, accuracy, or privacy practices of those third parties.</p>

      <PolicyNotice title="One simple promise">
        If anything we represent about a stone is later proven inaccurate by an independent laboratory, our Lifetime Authenticity Guarantee applies — we will repurchase the stone at the original sale price, in perpetuity.
      </PolicyNotice>
    </SimplePage>
  );
};

// ============================================================
//  Public manifest used by Header + Footer for the nav menu
// ============================================================
export const POLICY_LINKS = [
  { key: "authenticity",     label: "Authenticity",      path: "policies/authenticity" },
  { key: "certification",    label: "Certification",     path: "policies/certification" },
  { key: "warranty",         label: "Warranty",          path: "policies/warranty" },
  { key: "faq",              label: "FAQ",               path: "policies/faq" },
  { key: "ethical-sourcing", label: "Ethical Sourcing",  path: "policies/ethical-sourcing" },
  { key: "returns",          label: "Returns & Refunds", path: "policies/returns" },
  { key: "shipping",         label: "Shipping",          path: "policies/shipping" },
  { key: "auction-rules",    label: "Auction Rules",     path: "policies/auction-rules" },
  { key: "privacy",          label: "Privacy Policy",    path: "policies/privacy" },
  { key: "terms",            label: "Terms & Conditions",path: "policies/terms" },
  { key: "disclaimer",       label: "Disclaimer",        path: "policies/disclaimer" },
];

// Tiny re-export so a single import from Header works
export { ChevronDown as PolicyChevron };
